import React, { useState } from "react";
import { CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import { Link, useNavigate } from "react-router-dom";
import GifLogo from "../../../images/main-logo.gif";
import DurianLogo from "../../../images/durian-logo.png";
import Pencil from "../../../images/pencil.png";
import UserPool from "../../../UserPool";
import Loader from '../../../components/Loader';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState("password");
  const [showNewPassword, setShowNewPassword] = useState("password");
  const [showConfirmPassword, setShowConfirmPassword] = useState("password");
  const [formData, setFormData] = useState({
    username: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const username = formData.username.trim();
    if (!username || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (formData.newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError("New password must be different from current password");
      return;
    }

    setLoading(true);

    const user = new CognitoUser({
      Username: username,
      Pool: UserPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: formData.currentPassword,
    });

    user.authenticateUser(authDetails, {
      onSuccess: () => {
        // Authenticated successfully, now change password
        user.changePassword(formData.currentPassword, formData.newPassword, (err, result) => {
          setLoading(false);
          if (err) {
            if (err.code === "InvalidPasswordException") {
              setError(err.message || "Password does not meet requirements");
            } else if (err.code === "LimitExceededException") {
              setError("Too many attempts. Please try again later");
            } else {
              setError(err.message || "Failed to change password");
            }
            return;
          }
          setSuccess("Password changed successfully! Redirecting to login...");
          // Sign out after password change so user logs in with new password
          user.signOut();
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        });
      },
      onFailure: (err) => {
        setLoading(false);
        if (err.code === "NotAuthorizedException") {
          setError("Incorrect current password");
        } else if (err.code === "UserNotFoundException") {
          setError("No account found with this username/email");
        } else if (err.code === "UserNotConfirmedException") {
          setError("Please verify your email before changing password");
        } else {
          setError(err.message || "Authentication failed");
        }
      },
      newPasswordRequired: () => {
        // User has a temporary password — redirect to change-password page
        setLoading(false);
        navigate("/change-password", {
          state: {
            username: username,
            tempPassword: formData.currentPassword,
          },
        });
      },
    });
  };

  const EyeOpen = ({ onClick }) => (
    <svg onClick={onClick} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer">
      <path d="M15.5 12C15.5 13.933 13.933 15.5 12 15.5C10.067 15.5 8.5 13.933 8.5 12C8.5 10.067 10.067 8.5 12 8.5C13.933 8.5 15.5 10.067 15.5 12Z" stroke="#2F2F2F" />
      <path d="M20.188 10.9343C20.5762 11.4056 20.7703 11.6412 20.7703 12C20.7703 12.3588 20.5762 12.5944 20.188 13.0657C18.7679 14.7899 15.6357 18 12 18C8.36427 18 5.23206 14.7899 3.81197 13.0657C3.42381 12.5944 3.22973 12.3588 3.22973 12C3.22973 11.6412 3.42381 11.4056 3.81197 10.9343C5.23206 9.21014 8.36427 6 12 6C15.6357 6 18.7679 9.21014 20.188 10.9343Z" stroke="#2F2F2F" />
    </svg>
  );

  const EyeClosed = ({ onClick }) => (
    <svg onClick={onClick} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer">
      <path fillRule="evenodd" clipRule="evenodd" d="M15.9202 12.7988C15.9725 12.5407 16 12.2736 16 12C16 9.79086 14.2091 8 12 8C11.7264 8 11.4593 8.02746 11.2012 8.07977L12.1239 9.00251C13.6822 9.06583 14.9342 10.3178 14.9975 11.8761L15.9202 12.7988ZM9.39311 10.5143C9.14295 10.9523 9 11.4595 9 12C9 13.6569 10.3431 15 12 15C12.5405 15 13.0477 14.857 13.4857 14.6069L14.212 15.3332C13.5784 15.7545 12.8179 16 12 16C9.79086 16 8 14.2091 8 12C8 11.1821 8.24547 10.4216 8.66676 9.78799L9.39311 10.5143Z" fill="#2F2F2F" />
      <path fillRule="evenodd" clipRule="evenodd" d="M16.1537 17.2751L15.4193 16.5406C14.3553 17.1196 13.1987 17.5 12 17.5C10.3282 17.5 8.73816 16.7599 7.36714 15.7735C6.00006 14.79 4.89306 13.5918 4.19792 12.7478C3.77356 12.2326 3.72974 12.1435 3.72974 12C3.72974 11.8565 3.77356 11.7674 4.19792 11.2522C4.86721 10.4396 5.9183 9.29863 7.21572 8.33704L6.50139 7.62271C5.16991 8.63072 4.10383 9.79349 3.42604 10.6164L3.36723 10.6876C3.03671 11.087 2.72974 11.4579 2.72974 12C2.72974 12.5421 3.0367 12.913 3.36723 13.3124L3.42604 13.3836C4.15099 14.2638 5.32014 15.5327 6.78312 16.5853C8.24216 17.635 10.0361 18.5 12 18.5C13.5101 18.5 14.9196 17.9886 16.1537 17.2751ZM9.18993 6.06861C10.0698 5.71828 11.0135 5.5 12 5.5C13.9639 5.5 15.7579 6.365 17.2169 7.41472C18.6799 8.46727 19.849 9.73623 20.574 10.6164L20.6328 10.6876C20.9633 11.087 21.2703 11.4579 21.2703 12C21.2703 12.5421 20.9633 12.913 20.6328 13.3124L20.574 13.3836C20.0935 13.9669 19.418 14.721 18.5911 15.4697L17.883 14.7617C18.6787 14.0456 19.3338 13.3164 19.8021 12.7478C20.2265 12.2326 20.2703 12.1435 20.2703 12C20.2703 11.8565 20.2265 11.7674 19.8021 11.2522C19.107 10.4082 18 9.21001 16.6329 8.22646C15.2619 7.24007 13.6718 6.5 12 6.5C11.3056 6.5 10.6253 6.62768 9.96897 6.84765L9.18993 6.06861Z" fill="#2F2F2F" />
      <path d="M5 2L21 18" stroke="#2F2F2F" />
    </svg>
  );

  return (
    <div className="flex flex-col md:flex-row justify-center md:justify-between w-full h-auto min-h-screen bg-gradient-to-b from-primary-500 to-secondary-500 py-6 px-4 md:py-10 md:px-16">
      {isLoading && <Loader />}

      <div className="hidden md:flex flex-col gap-5 h-full">
        <div className="flex items-center gap-3">
          <img src={DurianLogo} alt="Durian Technologies" className="h-[50px]" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        <div className="flex justify-center w-[485px]">
          <img src={GifLogo} alt="main-logo" className="w-[350px]" />
        </div>
        <div className="w-[485px]">
          <h1 className="text-center text-text-light">
            <span className="text-[45px] font-bold">CMS crm</span>
            <br />
            <span className="text-[36px] whitespace-nowrap">Durian Technologies Inc.</span>
          </h1>
        </div>
      </div>

      <div className="flex flex-col justify-between items-center p-8 md:p-16 bg-white rounded-lg w-full md:w-[430px]">
        <form onSubmit={handleSubmit} className="flex flex-col justify-between items-center h-full w-full">
          <h1 className="text-4xl">Change Password</h1>

          <div>
            <div className="flex flex-col gap-6">
              {/* Username / Email */}
              <div>
                <label htmlFor="username" className="text-text-hint">
                  Username / Email<span className="text-red-500">*</span>
                </label>
                <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                  <input
                    id="username"
                    onChange={handleChange}
                    value={formData.username}
                    name="username"
                    type="text"
                    required
                    placeholder="Enter your username or email"
                    className="outline-none border-none focus:ring-0 flex-grow"
                  />
                  <img src={Pencil} alt="pencil" className="w-6 h-6" />
                </div>
              </div>

              {/* Current Password */}
              <div>
                <label htmlFor="currentPassword" className="text-text-hint">
                  Current Password<span className="text-red-500">*</span>
                </label>
                <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                  <input
                    id="currentPassword"
                    onChange={handleChange}
                    value={formData.currentPassword}
                    name="currentPassword"
                    type={showCurrentPassword}
                    required
                    placeholder="Enter current password"
                    className="outline-none border-none focus:ring-0 flex-grow"
                  />
                  {showCurrentPassword === "password" ? (
                    <EyeOpen onClick={() => setShowCurrentPassword("text")} />
                  ) : (
                    <EyeClosed onClick={() => setShowCurrentPassword("password")} />
                  )}
                </div>
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="text-text-hint">
                  New Password<span className="text-red-500">*</span>
                </label>
                <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                  <input
                    id="newPassword"
                    onChange={handleChange}
                    value={formData.newPassword}
                    name="newPassword"
                    type={showNewPassword}
                    required
                    placeholder="Enter new password"
                    className="outline-none border-none focus:ring-0 flex-grow"
                  />
                  {showNewPassword === "password" ? (
                    <EyeOpen onClick={() => setShowNewPassword("text")} />
                  ) : (
                    <EyeClosed onClick={() => setShowNewPassword("password")} />
                  )}
                </div>
              </div>

              {/* Confirm New Password */}
              <div>
                <label htmlFor="confirmPassword" className="text-text-hint">
                  Confirm New Password<span className="text-red-500">*</span>
                </label>
                <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                  <input
                    id="confirmPassword"
                    onChange={handleChange}
                    value={formData.confirmPassword}
                    name="confirmPassword"
                    type={showConfirmPassword}
                    required
                    placeholder="Confirm new password"
                    className="outline-none border-none focus:ring-0 flex-grow"
                  />
                  {showConfirmPassword === "password" ? (
                    <EyeOpen onClick={() => setShowConfirmPassword("text")} />
                  ) : (
                    <EyeClosed onClick={() => setShowConfirmPassword("password")} />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 text-xs text-text-hint">
              <p className="font-semibold mb-1">Password must contain:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>At least 8 characters</li>
                <li>One uppercase letter (A-Z)</li>
                <li>One lowercase letter (a-z)</li>
                <li>One number (0-9)</li>
                <li>One special character</li>
              </ul>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{success}</p>
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between gap-2 items-center w-full">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 px-4 text-base rounded-full text-white bg-secondary-600 hover:bg-secondary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
            >
              {isLoading ? "Changing Password..." : "Change Password"}
            </button>

            <p className="text-sm text-center">
              Back to{" "}
              <Link to="/login" className="text-info-500">
                Login
              </Link>
            </p>
            <p className="text-text-hint">or</p>
            <p>
              <Link to="/subscribe" className="text-info-500 font-bold">
                Subscribe
              </Link>{" "}
              for your Business
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdatePassword;
